import React, { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X, Search, ChevronDown } from 'lucide-react'

export const SimpleMultiSelect = ({
    value = [], // Array of selected strings or objects
    options = [], // Array of available strings or objects
    onChange, // Function handles value changes
    placeholder = 'Select options...',
    className,
    disabled = false,
    labelKey = '', // If options are objects, which key to use for display
    valueKey = ''  // If options are objects, which key to use for value
}) => {
    const [searchQuery, setSearchQuery] = useState('')
    const [showDropdown, setShowDropdown] = useState(false)
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 })
    
    const inputRef = useRef(null)
    const dropdownRef = useRef(null)

    const getOptionLabel = (option) => {
        if (typeof option === 'string') return option
        return option[labelKey] || option.label || ''
    }

    const getOptionValue = (option) => {
        if (typeof option === 'string') return option
        return option[valueKey] || option.value || option.id || ''
    }

    const filteredOptions = options.filter(option => {
        const label = getOptionLabel(option).toLowerCase()
        const query = searchQuery.toLowerCase()
        const isSelected = value.some(v => v === getOptionValue(option))
        return label.includes(query) && !isSelected
    })

    const handleSelect = (option) => {
        const val = getOptionValue(option)
        if (!value.includes(val)) {
            onChange([...value, val])
        }
        setSearchQuery('')
        setShowDropdown(false)
    }

    const handleRemove = (valToRemove) => {
        if (disabled) return
        onChange(value.filter(v => v !== valToRemove))
    }

    const updateDropdownPosition = () => {
        if (inputRef.current) {
            const rect = inputRef.current.getBoundingClientRect()
            setDropdownPosition({
                top: rect.bottom + window.scrollY + 4,
                left: rect.left + window.scrollX,
                width: rect.width
            })
        }
    }

    useEffect(() => {
        if (showDropdown) {
            updateDropdownPosition()
            window.addEventListener('scroll', updateDropdownPosition, true)
            window.addEventListener('resize', updateDropdownPosition)
        }
        return () => {
            window.removeEventListener('scroll', updateDropdownPosition, true)
            window.removeEventListener('resize', updateDropdownPosition)
        }
    }, [showDropdown])

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target) &&
                inputRef.current &&
                !inputRef.current.contains(event.target)
            ) {
                setShowDropdown(false)
            }
        }
        if (showDropdown) {
            document.addEventListener('mousedown', handleClickOutside)
        }
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [showDropdown])

    return (
        <div className={`relative ${className || ''}`}>
            {/* Selected Tags */}
            <div className="flex flex-wrap gap-2 mb-2">
                {value.map((v, index) => {
                    const option = options.find(opt => getOptionValue(opt) === v)
                    const label = option ? getOptionLabel(option) : v
                    return (
                        <span
                            key={v || index}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium"
                        >
                            {label}
                            <button
                                type="button"
                                onClick={() => handleRemove(v)}
                                disabled={disabled}
                                className="hover:bg-blue-200 rounded-full p-0.5 transition-colors disabled:opacity-50"
                            >
                                <X size={12} />
                            </button>
                        </span>
                    )
                })}
            </div>

            {/* Input / Control */}
            <div className="relative">
                <input
                    ref={inputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setShowDropdown(true)}
                    placeholder={value.length > 0 ? '' : placeholder}
                    disabled={disabled}
                    className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50"
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2 text-gray-400 pointer-events-none">
                    {searchQuery ? <Search size={16} /> : <ChevronDown size={16} />}
                </div>

                {/* Dropdown via Portal */}
                {showDropdown && createPortal(
                    <div
                        ref={dropdownRef}
                        className="fixed z-[9999] bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                        style={{
                            top: `${dropdownPosition.top}px`,
                            left: `${dropdownPosition.left}px`,
                            width: `${dropdownPosition.width}px`
                        }}
                    >
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option, idx) => (
                                <button
                                    key={idx}
                                    type="button"
                                    onClick={() => handleSelect(option)}
                                    className="w-full px-4 py-2 text-left hover:bg-blue-50 text-sm focus:bg-blue-50 focus:outline-none"
                                >
                                    {getOptionLabel(option)}
                                </button>
                            ))
                        ) : (
                            <div className="px-4 py-2 text-sm text-gray-500 italic">
                                No options found
                            </div>
                        )}
                    </div>,
                    document.body
                )}
            </div>
        </div>
    )
}

export default SimpleMultiSelect
